import { useRouter } from "next/router";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { usePayHook } from "../../../services/hooks/usePay";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { store } from "../../../services/redux/app/store";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  increasePendingOperations,
  pushToLoadingStack,
  setAnimatedGlobalBannerState,
  setLoading,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import {
  setCustomerShortId,
  setToken,
  setUserExtraInfo,
} from "../../../services/redux/slices/userInfoSlice";
import {
  setSavedAddresses,
  setSelectedAddress,
} from "../../../services/redux/slices/deliverySlice";
import * as apiCalls from "../../../utils/APICalls/APICalls";
import {
  getCookie,
  removeCookie,
  setCookie,
} from "../../../utils/CookieHelper";
import { LowErrorType } from "../../../utils/ErrorTypes";
import LS from "../../../utils/LocalStorage";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  LocalStorageEnum,
  PAYMENT_METHOD,
  PagesEnum,
  SOCKET_NAMESPACE,
  URL_PARAMS,
} from "../../../utils/enums";
import env from "../../../utils/env.config";
import {
  getPageLink,
  removeUrlParam,
} from "../../../utils/help/navigation/navigation";
import { getSignInReturnPage } from "../../../utils/help/navigation/signInReturn";
import { clearMetaSessionGuard } from "../../../utils/help/socialMediaPixels/metaSession";
import { monitoringIdentifiers } from "../../../utils/help/user/user";
import {
  getFranchiseSocket,
  getSocket,
  refreshSocketAuth,
} from "../../../utils/socket";
import { OrganizationCustomer } from "../../../utils/types";
import { AuthContextProps } from "./user.types";

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);
const { Provider } = AuthContext;

// How long the "Resend code" button stays disabled after a code is sent.
const RESEND_CODE_SECONDS = 60;

export function AuthProvider(props: { children: ReactNode }): JSX.Element {
  const auth = useAuthProvider();

  return <Provider value={auth}>{props.children}</Provider>;
}

export const useAuth: any = () => {
  return useContext(AuthContext);
};
// Provider hook that creates an auth object and handles it's state
const useAuthProvider = () => {
  const restaurantSocket = getSocket(
    SOCKET_NAMESPACE.RESTAURANT,
    env.restaurantId!
  );
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const franchiseSocket = getFranchiseSocket(
    SOCKET_NAMESPACE.FRANCHISE,
    selectedFranchise?.id.toString() || ""
  );
  const dispatch = useAppDispatch();
  const [user, setUser] = useState({} as Boolean);
  const [phoneNumber, setPhoneNumber] = useState(null as unknown as string);
  const [countryCode, setCountryCode] = useState(null as unknown as string);
  const [phoneError, setPhoneError] = useState(false);
  const [tooManyAttemptsError, setTooManyAttemptsError] = useState(false);
  const [loginFlag, setLoginFlag] = useState(false);
  // Cloudflare Turnstile token for the next OTP request. Single-use: set by the
  // <Turnstile/> widget (rendered on the login/verify screens) and consumed +
  // reset on each generateOtp call so a resend always sends a fresh token.
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  // Keep in step with the backend's MIN_SECONDS_BETWEEN_OTP_RESENDS (59s): it
  // silently declines to send a resend requested any sooner, so enabling the
  // button earlier would just burn a tap and show nothing.
  const [resendCode, setResendCode] = useState({
    isBtnDisabled: true,
    time: RESEND_CODE_SECONDS,
  });
  const router = useRouter();
  const { code } = router.query;
  const { t } = useTranslationI18();
  const pay = usePayHook();
  const addCustomerAnalytics = useCustomerAnalytics();

  const selectedFranchiseId = useAppSelector(
    (state) => state.config.selectedFranchise?.id
  );
  const mainFranchiseId = useAppSelector(
    (state) => state.config.restaurantInfo?.mainFranchiseId
  );
  const enrollmentPhoneNumberInfo = useAppSelector(
    (state) => state.userInfo.phoneNumberInfo
  );
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantInfo
  )?.config;
  const organizationCustomer = useAppSelector(
    (state) => state.partnerAccount.organizationCustomer
  );
  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cartV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer).cart;

  const userExtraInfo = useAppSelector((state) => state.userInfo.userExtraInfo);

  const tips = cartV2.tips;
  const paymentMethod = cartAndLoyaltyReducer.paymentMethod;

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);

  const franchiseId = selectedFranchiseId || mainFranchiseId;
  // Used only for check-otp's account-linking side effect — restaurantId is
  // synchronously available (unlike franchiseId, which depends on an async
  // restaurant-info fetch resolving first), so it's the more reliable id to
  // send here.
  const restaurantId = restaurantInfo?.id || parseInt(env.restaurantId!);

  const setPhone = (phone: string, countryCodeInput: string) => {
    let correctPhone = phone;
    if (phone.substring(0, 4) === "+200") {
      correctPhone = phone.replace("+200", "+20");
    }
    setPhoneNumber(correctPhone);
    setCountryCode(countryCodeInput);
  };

  useEffect(() => {
    if (router.query[URL_PARAMS.LOGIN]) {
      setLoginFlag(true);
    }
  }, []);

  useEffect(() => {
    if (code) {
      const phone = enrollmentPhoneNumberInfo.phoneNumber.replace("+", "");
      handlePhoneInputChange(`${phone}`, enrollmentPhoneNumberInfo.countryCode);

      return;
    }
    if (
      LS._get(LocalStorageEnum.PHONE_NUMBER) &&
      LS._get(LocalStorageEnum.COUNTRY_CODE)
    ) {
      setPhone(
        LS._get(LocalStorageEnum.PHONE_NUMBER),
        LS._get(LocalStorageEnum.COUNTRY_CODE)
      );
    }
  }, [enrollmentPhoneNumberInfo]);

  const handlePhoneInputChange = (value: string, countryCode: string) => {
    setPhone("+" + value, countryCode.toUpperCase());
    LS._set(LocalStorageEnum.PHONE_NUMBER, "+" + value);
    LS._set(LocalStorageEnum.COUNTRY_CODE, countryCode.toUpperCase());
    if (phoneError) {
      setPhoneError(false);
    }
    setTooManyAttemptsError(false);
  };

  const signOut = () => {
    removeCookie(COOKIE_ENUM.TOKEN);
    removeCookie(COOKIE_ENUM.TERMSANDCONDITIONS);
    const { selfService } = store.getState().franchiseStatus;
    const deviceNumber = LS._get(LocalStorageEnum.DEVICE_NUMBER);
    const paymentPosTimeout = LS._get(LocalStorageEnum.PAYMENT_POS_TIMEOUT);
    dispatch(setToken(undefined));
    clearMetaSessionGuard();
    // Explicitly drop the previous customer's delivery data. The delivery slice
    // is persisted, so on a shared device it could otherwise leak the prior
    // user's saved/selected address before the fresh login re-fetches (review:
    // shared-device staleness). Belt-and-suspenders alongside the storage clear.
    dispatch(setSelectedAddress(null));
    dispatch(setSavedAddresses([]));
    LS._clearItems([]);

    if (selfService) {
      LS._set(LocalStorageEnum.SELF_SERVICE, true);
      LS._set(LocalStorageEnum.DEVICE_NUMBER, deviceNumber);
      LS._set(LocalStorageEnum.PAYMENT_POS_TIMEOUT, paymentPosTimeout);
      const selfServiceUrl = getPageLink(router, PagesEnum.SelfService);
      window.location.replace(selfServiceUrl.as);

      return;
    }

    window.location.replace(getPageLink(router, PagesEnum.Home).as);
  };

  const ReturnAuthenticatedUser = () => {
    let user: { name: string; phone: string } | undefined;
    if (getCookie(COOKIE_ENUM.TOKEN)) {
      try {
        const token: string = getCookie(COOKIE_ENUM.TOKEN)?.split(
          "."
        )[1] as string;
        const tokenJson = JSON.parse(
          Buffer.from(token!, "base64").toString("utf8")
        );
        user = { name: tokenJson.person_name, phone: tokenJson.phone_number };
      } catch (e) {
        console.log("CATCH", e);
        dispatch(setToken(undefined));
        removeCookie(COOKIE_ENUM.TOKEN);
        clearMetaSessionGuard();
        user = undefined;
      }
    } else {
      user = undefined;
    }

    return user;
  };

  const signIn = async (resendCode?: boolean, offlineRedeemLogin?: boolean) => {
    if (restaurantConfig?.loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.INCREASE,
          functionName: "useAuth signIn",
          date: new Date(),
        })
      );
      dispatch(increasePendingOperations());
    } else {
      dispatch(setLoading(true));
    }

    const name = LS._get(LocalStorageEnum.USER_NAME) || undefined;

    if (!loginFlag && (!name || name.length === 0)) {
      dispatch(
        setAnimatedGlobalBannerState({
          isOpen: true,
          text: t("login:name-required"),
          warning: true,
        })
      );

      return;
    }

    if (
      (!phoneNumber || phoneNumber.length === 0) &&
      (phoneError || !tooManyAttemptsError)
    ) {
      dispatch(
        setAnimatedGlobalBannerState({
          isOpen: true,
          text: t("login:login-invalid-number"),
          warning: true,
        })
      );

      return;
    } else if (
      (!phoneNumber || phoneNumber.length === 0) &&
      tooManyAttemptsError &&
      !phoneError
    ) {
      dispatch(
        setAnimatedGlobalBannerState({
          isOpen: true,
          text: t("login:login-too-many-attempts"),
          warning: true,
        })
      );

      return;
    }

    if (!franchiseId) {
      return console.warn("No franchise selected", franchiseId);
    }

    return await apiCalls
      .generateOtp(
        phoneNumber,
        franchiseId,
        countryCode,
        name,
        code?.toString(),
        loginFlag,
        turnstileToken || undefined
      )
      .then((res) => {
        // Turnstile tokens are single-use. Clear the consumed token so the
        // widget re-arms and a subsequent send (resend) gets a fresh one.
        setTurnstileToken("");
        if (resendCode) {
          setResendCode({
            isBtnDisabled: true,
            time: RESEND_CODE_SECONDS,
          });
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: t("login:resend"),
              warning: false,
            })
          );
          if (restaurantConfig?.loadingStateSolution) {
            dispatch(
              pushToLoadingStack({
                action: LoadingActionEnum.DECREASE,
                functionName: "useAuth signIn",
                date: new Date(),
              })
            );
            dispatch(decreasePendingOperations());
          } else {
            dispatch(setLoading(false));
          }

          return;
        }
        if (res.status === 204) {
          const queryParams = [];
          if (loginFlag) {
            queryParams.push(`${URL_PARAMS.LOGIN}=true`);
          }
          if (router.query.pay === "true") {
            queryParams.push(`${URL_PARAMS.PAY}=true`);
          }
          if (router.query.confirm === "true") {
            queryParams.push(`${URL_PARAMS.CONFIRM}=true`);
          }
          if (router.query.offline_mode === "true") {
            queryParams.push(`${URL_PARAMS.OFFLINE_MODE}=true`);
          }
          if (!offlineRedeemLogin) {
            const isFromAppleWallet =
              typeof window !== "undefined" &&
              sessionStorage.getItem("fromAppleWallet") === "true";
            const isFromGoogleWallet =
              typeof window !== "undefined" &&
              sessionStorage.getItem("fromGoogleWallet") === "true";

            if (isFromAppleWallet) {
              queryParams.push("from=apple-wallet");
            }
            if (isFromGoogleWallet) {
              queryParams.push("from=google-wallet");
            }
            if (router.query.returnTo) {
              queryParams.push(`returnTo=${router.query.returnTo}`);
            }

            router.push(
              `${
                (getPageLink(router, PagesEnum.Verify, {
                  queryParams,
                }).href,
                getPageLink(router, PagesEnum.Verify, {
                  queryParams,
                }).as)
              }`
            );

            addCustomerAnalytics(
              CUSTOMER_ANALYTICS_EVENT.ENTERED_PHONE_NUMBER,
              {
                correct: true,
                login: !!loginFlag,
              }
            );
          }
        }
        if (res.data.token) {
          postGetCustomerToken(res.data.token, res.data.organizationCustomer);
        }
        if (restaurantConfig?.loadingStateSolution) {
          dispatch(
            pushToLoadingStack({
              action: LoadingActionEnum.DECREASE,
              functionName: "useAuth signIn",
              date: new Date(),
            })
          );
          dispatch(decreasePendingOperations());
        } else {
          dispatch(setLoading(false));
        }
      })

      .catch((e) => {
        // The token (single-use) is now spent even on failure; clear it so the
        // widget re-arms with a fresh token for retry.
        setTurnstileToken("");
        // WAF geo allow-list block: requests to /auth from a source IP outside
        // the allowed country (EG) are rejected at the edge with a 403 carrying
        // a { error: "GEO_BLOCKED" } marker body and CORS headers (see
        // terraform-modules prepits-ecs-env-stack/waf.tf), so we can reliably
        // show a VPN hint instead of the misleading "invalid number" fallback.
        if (e.response?.data?.error === "GEO_BLOCKED") {
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: t("login:login-disable-vpn"),
              warning: true,
            })
          );
        } else if (
          e.response?.data?.error ===
            LowErrorType.AuthController_GenerateOTP_30_UNAUTHORTIZED_IP_OTP ||
          e.response?.data?.error ===
            LowErrorType.AuthService_GenerateOTP_FIVE_WRONG_OTP
        ) {
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: t("login:login-too-many-attempts"),
              warning: true,
            })
          );
        } else if (
          e.response?.data?.error ===
          LowErrorType.UserService_generateUserTokenWithName_NoNameProvidedForNewCustomer
        ) {
          setLoginFlag(false);
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: t("login:error-enter-name-again"),
              warning: true,
            })
          );
        } else if (
          e.response?.data?.error ===
          LowErrorType.AuthController_OTP_PersonNotFound
        ) {
          setLoginFlag(false);
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: t("login:please-register"),
              warning: true,
            })
          );
        } else if (
          e.response?.data?.error ===
          LowErrorType.AuthController_OTP_PersonWithoutName
        ) {
          setLoginFlag(false);
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: t("login:name-required"),
              warning: true,
            })
          );
        } else {
          // Only call the number invalid when the backend says so. Anything
          // else (a 401 from a lost session, a network drop, an unexpected
          // error) used to land here too and told customers with a perfectly
          // good number that it was wrong — they kept retyping it.
          const isPhoneRejected =
            e.response?.data?.error ===
              LowErrorType.AuthController_SentOtp_InvalidPhoneNumber ||
            e.response?.status === 400 ||
            e.response?.status === 422;
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: isPhoneRejected
                ? t("login:login-invalid-number")
                : t("login:resend-error"),
              warning: true,
            })
          );
        }
        addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ENTERED_PHONE_NUMBER, {
          correct: false,
          login: !!loginFlag,
          // Error shape so otherwise-silent OTP failures are diagnosable in
          // analytics without needing a local repro or backend logs.
          additionalDetails: {
            hasResponse: !!e.response, // false => request never reached backend
            httpStatus: e.response?.status ?? 0,
            backendError:
              e.response?.data?.error?.message ??
              e.response?.data?.error ??
              null,
            errName: e.name, // "SyntaxError"/"TypeError" => client-side throw
            errCode: e.code, // "ERR_NETWORK"/"ECONNABORTED" => network failure
            sentTurnstile: !!turnstileToken,
            phoneLen: phoneNumber?.length ?? 0,
          },
        });
        if (restaurantConfig?.loadingStateSolution) {
          dispatch(
            pushToLoadingStack({
              action: LoadingActionEnum.DECREASE,
              functionName: "useAuth signIn",
              date: new Date(),
            })
          );
          dispatch(decreasePendingOperations());
        } else {
          dispatch(setLoading(false));
        }
      });
  };

  const verifyOtp = async (code: string, name?: string) => {
    if (restaurantConfig?.loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.INCREASE,
          functionName: "useAuth verifyOtp",
          date: new Date(),
        })
      );
      dispatch(increasePendingOperations());
    } else {
      dispatch(setLoading(true));
    }

    await apiCalls
      .verifyOtpCode(phoneNumber, code, name, userExtraInfo, restaurantId)
      .then((res) => {
        addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ENTERED_OTP, {
          correct: true,
          login: !name,
        });
        postGetCustomerToken(res.data.token, res.data.organizationCustomer);
        if (res.data.token) {
          monitoringIdentifiers(res.data.token);
        }
      })
      .catch((error) => {
        console.log({ error });
        addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ENTERED_OTP, {
          correct: false,
          login: !name,
        });
        dispatch(
          setAnimatedGlobalBannerState({
            isOpen: true,
            text: tooManyAttemptsError
              ? t("login:login-too-many-attempts")
              : t("login:login-wrong-code"),
            warning: true,
          })
        );
        // setCodeError(true);
      })
      .finally(() => {
        if (restaurantConfig?.loadingStateSolution) {
          dispatch(
            pushToLoadingStack({
              action: LoadingActionEnum.DECREASE,
              functionName: "useAuth verifyOtp",
              date: new Date(),
            })
          );
          dispatch(decreasePendingOperations());
        } else {
          dispatch(setLoading(false));
        }
      });
  };

  const postGetCustomerToken = useCallback(
    (token: string, resOrganizationCustomer?: OrganizationCustomer) => {
      removeUrlParam(URL_PARAMS.LOGIN);
      setCookie(COOKIE_ENUM.TOKEN, token);
      dispatch(setToken(token));
      // The Meta identifiers collected so far are stored against the *visitor*
      // customer id this token is replacing. Forget the once-a-day guard so the
      // real customer gets a row of their own on the next page view.
      clearMetaSessionGuard();
      LS._delete(LocalStorageEnum.USER_NAME);
      LS._delete(LocalStorageEnum.PHONE_NUMBER);
      LS._delete(LocalStorageEnum.COUNTRY_CODE);
      dispatch(setUserExtraInfo(undefined));
      dispatch(setCustomerShortId(undefined));

      refreshSocketAuth();
      if (router.query.partnerlogin === "true") {
        delete router.query.partnerlogin;
        if (resOrganizationCustomer && resOrganizationCustomer?.verified) {
          router.push(
            getPageLink(router, PagesEnum.Home).href,
            getPageLink(router, PagesEnum.Home).as
          );
        } else {
          router.query.verify = "true";
          router.query.email = organizationCustomer?.mail;
          router.push(
            getPageLink(router, PagesEnum.PartnerAccount).href,
            getPageLink(router, PagesEnum.PartnerAccount).as
          );
        }

        return;
      }

      const returnPage = getSignInReturnPage(router.query.returnTo);
      if (router.query.pay === "true") {
        if (restaurantConfig?.loadingStateSolution) {
          dispatch(
            pushToLoadingStack({
              action: LoadingActionEnum.DECREASE,
              functionName: "useAuth postGetCustomerToken",
              date: new Date(),
            })
          );
          dispatch(decreasePendingOperations());
        } else {
          dispatch(setLoading(false));
        }
        pay(
          "/verify-phone",
          restaurantInfo?.config?.signUpFeature
            ? PAYMENT_METHOD.CASH
            : paymentMethod,
          tips
        );
      } else if (router.query.confirm === "true") {
        router.push(
          getPageLink(router, PagesEnum.Tip).href,
          getPageLink(router, PagesEnum.Tip).as
        );
      } else if (router.query.offline_mode === "true") {
        router.push(
          getPageLink(router, PagesEnum.OfflineMode).href,
          getPageLink(router, PagesEnum.OfflineMode).as
        );
      } else if (returnPage) {
        router.push(
          getPageLink(router, returnPage).href,
          getPageLink(router, returnPage).as
        );
      } else {
        const targetPage: PagesEnum = PagesEnum.Home;
        // Check both pathname and asPath to catch wallet flow pages
        // Also check window.location as fallback since router might not be updated yet
        // AND check sessionStorage flag as ultimate fallback
        const pathname = router.pathname || "";
        const asPath = router.asPath || "";
        const windowPath =
          typeof window !== "undefined" ? window.location.pathname : "";
        const currentPath = asPath || pathname || windowPath;

        const isFromAppleWallet =
          typeof window !== "undefined" &&
          sessionStorage.getItem("fromAppleWallet") === "true";
        const isFromGoogleWallet =
          typeof window !== "undefined" &&
          sessionStorage.getItem("fromGoogleWallet") === "true";
        const isWalletFlow =
          isFromAppleWallet ||
          isFromGoogleWallet ||
          pathname.includes(PagesEnum.AppleWallet) ||
          pathname.includes(PagesEnum.AppleWalletConfirmation) ||
          pathname.includes(PagesEnum.AppleWalletLogin) ||
          pathname.includes(PagesEnum.AppleWalletVerify) ||
          asPath.includes(`/${PagesEnum.AppleWallet}`) ||
          asPath.includes(`/${PagesEnum.AppleWalletConfirmation}`) ||
          asPath.includes(`/${PagesEnum.AppleWalletLogin}`) ||
          asPath.includes(`/${PagesEnum.AppleWalletVerify}`) ||
          windowPath.includes(`/${PagesEnum.AppleWallet}`) ||
          windowPath.includes(`/${PagesEnum.AppleWalletConfirmation}`) ||
          windowPath.includes(`/${PagesEnum.AppleWalletLogin}`) ||
          windowPath.includes(`/${PagesEnum.AppleWalletVerify}`) ||
          currentPath.includes(`/${PagesEnum.AppleWallet}`) ||
          currentPath.includes(`/${PagesEnum.AppleWalletConfirmation}`) ||
          currentPath.includes(`/${PagesEnum.AppleWalletLogin}`) ||
          currentPath.includes(`/${PagesEnum.AppleWalletVerify}`);

        // Don't redirect if we're on wallet flow pages or offline burning - let them handle the flow
        if (isWalletFlow) {
          // On wallet flow or offline burning - don't redirect, let the screen handle it
          console.log(
            "Skipping redirect - on wallet flow or offline burning page:",
            {
              pathname,
              asPath,
              windowPath,
              currentPath,
              isWalletFlow,
            }
          );
        } else {
          router.push(
            getPageLink(router, targetPage).href,
            getPageLink(router, targetPage).as
          );
        }
      }
    },
    [
      router,
      restaurantConfig,
      restaurantInfo,
      pay,
      paymentMethod,
      tips,
      organizationCustomer,
      dispatch,
      restaurantSocket,
      franchiseSocket,
    ]
  );

  return {
    user,
    phone: phoneNumber,
    setPhone,
    phoneError,
    tooManyAttemptsError,
    setTooManyAttemptsError,
    setPhoneError,
    signOut,
    setUser,
    ReturnAuthenticatedUser: ReturnAuthenticatedUser,
    signIn,
    verifyOtp,
    handlePhoneInputChange,
    loginFlag,
    setLoginFlag,
    resendCode,
    setResendCode,
    turnstileToken,
    setTurnstileToken,
  };
};
